import React from "react";

// Everything the conversation record already carries about the visitor, laid
// out in full. The right-hand lead panel shows the same data trimmed to what
// fits beside the transcript; this is the unabridged view, so nothing the API
// sends is only visible by hovering something.

const EMPTY = "—";

const value = (raw) => {
  if (raw === null || raw === undefined || raw === "") return EMPTY;
  return String(raw);
};

const Field = ({ label, children }) => (
  <div className="conv-details-field">
    <span className="conv-details-label">{label}</span>
    <span className="conv-details-value">{children}</span>
  </div>
);

// A group with nothing in it is noise, so it is dropped rather than rendered
// as a heading over five dashes.
const hasAny = (...values) =>
  values.some((entry) => entry !== null && entry !== undefined && entry !== "");

export default function DetailsTab({ conversation }) {
  const lead = conversation?.lead || {};
  const qualified = lead.qualified || {};
  const qualifiedEntries = Object.entries(qualified).filter(
    ([, answer]) => answer !== null && answer !== undefined && answer !== "",
  );

  const sourceLabel =
    conversation?.source === "training"
      ? "Training session"
      : conversation?.source === "widget"
        ? "Website widget"
        : value(conversation?.source);

  return (
    <div className="conv-details">
      {/* The request the assistant passed on, when there was one. */}
      {lead.summary && (
        <section className="conv-details-group">
          <h3 className="conv-details-title">What they want</h3>
          <p className="conv-details-summary">{lead.summary}</p>
        </section>
      )}

      <section className="conv-details-group">
        <h3 className="conv-details-title">Visitor</h3>
        <div className="conv-details-grid">
          <Field label="Name">{value(conversation?.name)}</Field>
          <Field label="Email">
            {lead.email ? (
              <a href={`mailto:${lead.email}`}>{lead.email}</a>
            ) : (
              EMPTY
            )}
          </Field>
          <Field label="Phone">
            {lead.phone ? <a href={`tel:${lead.phone}`}>{lead.phone}</a> : EMPTY}
          </Field>
          <Field label="Location">{value(lead.location)}</Field>
          <Field label="Device">{value(lead.device)}</Field>
        </div>
      </section>

      {hasAny(lead.company, lead.jobTitle, lead.website) && (
        <section className="conv-details-group">
          <h3 className="conv-details-title">Company</h3>
          <div className="conv-details-grid">
            <Field label="Company">{value(lead.company)}</Field>
            <Field label="Job title">{value(lead.jobTitle)}</Field>
            <Field label="Website">
              {lead.website ? (
                <a
                  href={`https://${String(lead.website).replace(/^https?:\/\//i, "")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {lead.website}
                </a>
              ) : (
                EMPTY
              )}
            </Field>
          </div>
        </section>
      )}

      <section className="conv-details-group">
        <h3 className="conv-details-title">Conversation</h3>
        <div className="conv-details-grid">
          <Field label="Started">{value(lead.firstSeen || conversation?.date)}</Field>
          <Field label="Last activity">{value(conversation?.time)}</Field>
          <Field label="Source">{sourceLabel}</Field>
          <Field label="Landing page">{value(lead.sourcePage)}</Field>
          <Field label="UTM source">{value(lead.utmSource)}</Field>
          <Field label="Lead score">
            {/* 0 is a real score, so this can't test truthiness. */}
            {lead.score === "" || lead.score === null || lead.score === undefined
              ? EMPTY
              : lead.score}
          </Field>
          <Field label="Status">{value(conversation?.status)}</Field>
          <Field label="Tags">
            {conversation?.tags?.length ? (
              <span className="conv-details-tags">
                {conversation.tags.map((tag) => (
                  <span className="conv-tag-chip" key={tag}>
                    {tag}
                  </span>
                ))}
              </span>
            ) : (
              EMPTY
            )}
          </Field>
        </div>
      </section>

      {qualifiedEntries.length > 0 && (
        <section className="conv-details-group">
          <h3 className="conv-details-title">Qualifying answers</h3>
          <div className="conv-details-grid">
            {qualifiedEntries.map(([question, answer]) => (
              <Field key={question} label={question}>
                {typeof answer === "object" ? JSON.stringify(answer) : String(answer)}
              </Field>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
